import React, { useState, useEffect, useCallback } from 'react';
import { projectRequestsApi, ProjectRequest } from '../../api/projectRequestsApi';
import { lateEntryRequestsApi, LateEntryRequest } from '../../api/lateEntryRequestsApi';
import { usersApi } from '../../api/usersApi';
import { projectsApi, AssignmentHistoryItem } from '../../api/projectsApi';
import { User } from '../../types';
import { Button } from '../../components/common/Button';
import { Card } from '../../components/common/Card';
import { Loader } from '../../components/common/Loader';
import { Icons } from '../../components/common/Icons';
import './Allocations.css';
import { useSearchParams } from 'react-router-dom';



export function Allocations() {

  // ✅ Step 1: get params FIRST
  const [searchParams] = useSearchParams();
  const tabFromURL = searchParams.get('tab') as TabType | null;

  // ✅ Step 2: then use it
  const [activeTab, setActiveTab] = useState<TabType>(
    tabFromURL || 'project-requests'
  );

  // ✅ Step 3: keep it synced (important)
  useEffect(() => {
    if (tabFromURL) {
      setActiveTab(tabFromURL);
    }
  }, [tabFromURL]);


type TabType = 'project-requests' | 'assignment-history' | 'late-entries' | 'unassigned';
type HistoryStatusFilter = 'all' | 'active' | 'removed';


  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<number | null>(null); // Track which request is being processed
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Project Requests
  const [projectRequests, setProjectRequests] = useState<ProjectRequest[]>([]);
  
  // Late Entry Requests
  const [lateEntryRequests, setLateEntryRequests] = useState<LateEntryRequest[]>([]);
  
  // Unassigned Employees
  const [unassignedEmployees, setUnassignedEmployees] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Assignment History
  const [historyItems, setHistoryItems] = useState<AssignmentHistoryItem[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPages, setHistoryPages] = useState(1);
  const [historyStatus, setHistoryStatus] = useState<HistoryStatusFilter>('all');
  const [historySearch, setHistorySearch] = useState('');
  
  // Reject modal
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectingRequest, setRejectingRequest] = useState<{type: 'project' | 'late-entry', id: number} | null>(null);

  // Fetch all tab counts once on mount so badges are accurate immediately
  const [countsLoaded, setCountsLoaded] = useState(false);
  
  useEffect(() => {
    if (countsLoaded) return;
    const loadAllCounts = async () => {
      try {
        const [prRes, leRes, uaRes] = await Promise.all([
          projectRequestsApi.getRequests({ status: 'pending' }),
          lateEntryRequestsApi.getRequests({ status: 'pending' }),
          usersApi.getUnassignedEmployees({}),
        ]);
        setProjectRequests(prRes.requests);
        setLateEntryRequests(leRes.requests);
        setUnassignedEmployees(uaRes.employees);
        setCountsLoaded(true);
      } catch {
        // counts will load when tab is clicked
      }
    };
    loadAllCounts();
  }, [countsLoaded]);

  const fetchData = useCallback(async (skipLoading = false) => {
    try {
      if (!skipLoading) setLoading(true);
      setError(null);
      
      if (activeTab === 'project-requests') {
        const res = await projectRequestsApi.getRequests({ status: 'pending' });
        setProjectRequests(res.requests);
      } else if (activeTab === 'late-entries') {
        const res = await lateEntryRequestsApi.getRequests({ status: 'pending' });
        setLateEntryRequests(res.requests);
      } else if (activeTab === 'assignment-history') {
        const res = await projectsApi.getAssignmentHistory({
          page: historyPage,
          per_page: 25,
          status: historyStatus,
          search: historySearch || undefined,
        });
        setHistoryItems(res.assignments);
        setHistoryTotal(res.total);
        setHistoryPages(res.pages);
      } else {
        const res = await usersApi.getUnassignedEmployees({ search: searchTerm || undefined });
        setUnassignedEmployees(res.employees);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [activeTab, searchTerm, historyPage, historyStatus, historySearch]);

  // Fetch on tab change (with loading spinner)
  useEffect(() => {
    fetchData();
  }, [activeTab]); // eslint-disable-line

  // Search-as-you-type for unassigned tab (no loading spinner)
  useEffect(() => {
    if (activeTab !== 'unassigned') return;
    const timeout = setTimeout(() => fetchData(true), 300);
    return () => clearTimeout(timeout);
  }, [searchTerm]); // eslint-disable-line

  // Search/filter changes for assignment history (debounced, no spinner)
  useEffect(() => {
    if (activeTab !== 'assignment-history') return;
    const timeout = setTimeout(() => fetchData(true), 300);
    return () => clearTimeout(timeout);
  }, [historySearch, historyStatus, historyPage]); // eslint-disable-line

  // Project Request Handlers
  const handleApproveProjectRequest = async (requestId: number) => {
    if (actionLoading) return; // Prevent double-clicks
    try {
      setActionLoading(requestId);
      setError(null);
      await projectRequestsApi.approveRequest(requestId);
      setProjectRequests(prev => prev.filter(r => r.id !== requestId));
      setSuccess('Project request approved successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to approve request';
      // If already approved/rejected, still remove from list as it's no longer pending
      if (errorMsg.toLowerCase().includes('already')) {
        setProjectRequests(prev => prev.filter(r => r.id !== requestId));
      }
      setError(errorMsg);
      setTimeout(() => setError(null), 5000);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectProjectRequest = async () => {
    if (!rejectingRequest || rejectingRequest.type !== 'project') return;
    const reqId = rejectingRequest.id;
    if (actionLoading) return;
    
    try {
      setActionLoading(reqId);
      setError(null);
      await projectRequestsApi.rejectRequest(reqId, rejectReason);
      setProjectRequests(prev => prev.filter(r => r.id !== reqId));
      setShowRejectModal(false);
      setRejectReason('');
      setRejectingRequest(null);
      setSuccess('Project request rejected');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to reject request';
      if (errorMsg.toLowerCase().includes('already')) {
        setProjectRequests(prev => prev.filter(r => r.id !== reqId));
        setShowRejectModal(false);
        setRejectingRequest(null);
      }
      setError(errorMsg);
      setTimeout(() => setError(null), 5000);
    } finally {
      setActionLoading(null);
    }
  };

  // Late Entry Request Handlers
  const handleApproveLateEntry = async (requestId: number) => {
    if (actionLoading) return;
    try {
      setActionLoading(requestId);
      setError(null);
      await lateEntryRequestsApi.approveRequest(requestId);
      setLateEntryRequests(prev => prev.filter(r => r.id !== requestId));
      setSuccess('Late entry request approved');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to approve request';
      if (errorMsg.toLowerCase().includes('already')) {
        setLateEntryRequests(prev => prev.filter(r => r.id !== requestId));
      }
      setError(errorMsg);
      setTimeout(() => setError(null), 5000);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectLateEntry = async () => {
    if (!rejectingRequest || rejectingRequest.type !== 'late-entry') return;
    const reqId = rejectingRequest.id;
    if (actionLoading) return;
    
    try {
      setActionLoading(reqId);
      setError(null);
      await lateEntryRequestsApi.rejectRequest(reqId, rejectReason);
      setLateEntryRequests(prev => prev.filter(r => r.id !== reqId));
      setShowRejectModal(false);
      setRejectReason('');
      setRejectingRequest(null);
      setSuccess('Late entry request rejected');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to reject request';
      if (errorMsg.toLowerCase().includes('already')) {
        setLateEntryRequests(prev => prev.filter(r => r.id !== reqId));
        setShowRejectModal(false);
        setRejectingRequest(null);
      }
      setError(errorMsg);
      setTimeout(() => setError(null), 5000);
    } finally {
      setActionLoading(null);
    }
  };

  const openRejectModal = (type: 'project' | 'late-entry', id: number) => {
    setRejectingRequest({ type, id });
    setRejectReason('');
    setShowRejectModal(true);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const tabCounts = {
    'project-requests': projectRequests.length,
    'late-entries': lateEntryRequests.length,
    'unassigned': unassignedEmployees.length
  };

  return (
    <div className="allocations-page">
      <div className="allocations-header">
        <div className="allocations-title">
          <h1>Allocations & Requests</h1>
          <p>Manage project requests, late entries, and employee assignments</p>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <Icons.AlertCircle />
          <span>{error}</span>
          <button onClick={() => setError(null)}>&times;</button>
        </div>
      )}

      {success && (
        <div className="success-banner">
          <Icons.Check />
          <span>{success}</span>
          <button onClick={() => setSuccess(null)}>&times;</button>
        </div>
      )}

      <div className="tabs-container">
        <div className="tabs">
          <button
            className={`tab ${activeTab === 'project-requests' ? 'active' : ''}`}
            onClick={() => setActiveTab('project-requests')}
          >
            <Icons.Folder />
            <span>Project Requests</span>
            {tabCounts['project-requests'] > 0 && (
              <span className="tab-count">{tabCounts['project-requests']}</span>
            )}
          </button>
          <button
            className={`tab ${activeTab === 'assignment-history' ? 'active' : ''}`}
            onClick={() => setActiveTab('assignment-history')}
          >
            <Icons.History />
            <span>Assignment History</span>
          </button>
          <button
            className={`tab ${activeTab === 'late-entries' ? 'active' : ''}`}
            onClick={() => setActiveTab('late-entries')}
          >
            <Icons.Clock />
            <span>Late Entries</span>
            {tabCounts['late-entries'] > 0 && (
              <span className="tab-count">{tabCounts['late-entries']}</span>
            )}
          </button>
          <button
            className={`tab ${activeTab === 'unassigned' ? 'active' : ''}`}
            onClick={() => setActiveTab('unassigned')}
          >
            <Icons.Users />
            <span>Unassigned Employees</span>
            {tabCounts['unassigned'] > 0 && (
              <span className="tab-count">{tabCounts['unassigned']}</span>
            )}
          </button>
        </div>
      </div>

      <Card>
        {loading ? (
          <Loader text="Loading..." />
        ) : (
          <>
            {/* Project Requests Tab */}
            {activeTab === 'project-requests' && (
              <div className="tab-content">
                {projectRequests.length === 0 ? (
                  <div className="empty-state">
                    <Icons.Folder />
                    <p>No pending project requests</p>
                  </div>
                ) : (
                  <div className="requests-list">
                    {projectRequests.map(request => (
                      <div key={request.id} className="request-card">
                        <div className="request-info">
                          <div className="request-header">
                            <span className="employee-name">{request.employee_name}</span>
                            <span className="request-date">{formatDate(request.created_at)}</span>
                          </div>
                          <div className="request-details">
                            <span className="project-name">
                              <Icons.Folder /> {request.project_name}
                            </span>
                          </div>
                          {request.reason && (
                            <p className="request-reason">{request.reason}</p>
                          )}
                        </div>
                        <div className="request-actions">
                          <Button
                            size="sm"
                            onClick={() => handleApproveProjectRequest(request.id)}
                            disabled={actionLoading === request.id}
                          >
                            <Icons.Check /> {actionLoading === request.id ? 'Processing...' : 'Approve'}
                          </Button>
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => openRejectModal('project', request.id)}
                            disabled={actionLoading === request.id}
                          >
                            <Icons.X /> Reject
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Late Entry Requests Tab */}
            {activeTab === 'late-entries' && (
              <div className="tab-content">
                {lateEntryRequests.length === 0 ? (
                  <div className="empty-state">
                    <Icons.Clock />
                    <p>No pending late entry requests</p>
                  </div>
                ) : (
                  <div className="requests-list">
                    {lateEntryRequests.map(request => (
                      <div key={request.id} className="request-card">
                        <div className="request-info">
                          <div className="request-header">
                            <span className="employee-name">{request.employee_name}</span>
                            <span className="request-date">{formatDate(request.created_at)}</span>
                          </div>
                          <div className="request-details">
                            <span className="requested-date">
                              <Icons.Calendar /> For: {formatDate(request.requested_date)}
                            </span>
                          </div>
                          <p className="request-reason">{request.reason}</p>
                        </div>
                        <div className="request-actions">
                          <Button
                            size="sm"
                            onClick={() => handleApproveLateEntry(request.id)}
                            disabled={actionLoading === request.id}
                          >
                            <Icons.Check /> {actionLoading === request.id ? 'Processing...' : 'Approve'}
                          </Button>
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => openRejectModal('late-entry', request.id)}
                            disabled={actionLoading === request.id}
                          >
                            <Icons.X /> Reject
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Assignment History Tab */}
            {activeTab === 'assignment-history' && (
              <div className="tab-content">
                <div className="search-toolbar">
                  <div className="search-input-wrapper">
                    <Icons.Search className="search-icon" />
                    <input
                      type="text"
                      placeholder="Search by employee or project..."
                      value={historySearch}
                      onChange={(e) => { setHistoryPage(1); setHistorySearch(e.target.value); }}
                    />
                  </div>
                  <select
                    value={historyStatus}
                    onChange={(e) => { setHistoryPage(1); setHistoryStatus(e.target.value as HistoryStatusFilter); }}
                    className="history-status-select"
                  >
                    <option value="all">All</option>
                    <option value="active">Active</option>
                    <option value="removed">Removed</option>
                  </select>
                </div>

                {historyItems.length === 0 ? (
                  <div className="empty-state">
                    <Icons.History />
                    <p>No assignment history found</p>
                  </div>
                ) : (
                  <>
                    <div className="requests-list">
                      {historyItems.map(item => (
                        <div key={item.id} className="request-card">
                          <div className="request-info">
                            <div className="request-header">
                              <span className="employee-name">{item.employee_name || '-'}</span>
                              <span className="request-date">{formatDateTime(item.assigned_at)}</span>
                            </div>
                            <div className="request-details">
                              <span className="project-name">
                                <Icons.Folder /> {item.project_name || '-'}
                              </span>
                              {item.employee_code && (
                                <span className="employee-code">{item.employee_code}</span>
                              )}
                              {item.assigned_by_name && (
                                <span className="requested-date">
                                  <Icons.Users /> Assigned by {item.assigned_by_name}
                                </span>
                              )}
                              {item.start_date && (
                                <span className="requested-date">
                                  <Icons.Calendar /> Start: {formatDate(item.start_date)}
                                </span>
                              )}
                              {item.end_date && (
                                <span className="requested-date">
                                  <Icons.Calendar /> End: {formatDate(item.end_date)}
                                </span>
                              )}
                              {item.removed_at && (
                                <span className="requested-date">
                                  <Icons.X /> Removed: {formatDateTime(item.removed_at)}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="request-actions">
                            <span className={`status-badge ${item.is_active ? 'status-active' : 'status-removed'}`}>
                              {item.is_active ? 'Active' : 'Removed'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                    {historyPages > 1 && (
                      <div className="history-pagination">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => setHistoryPage(p => Math.max(1, p - 1))}
                          disabled={historyPage <= 1}
                        >
                          Previous
                        </Button>
                        <span className="history-pagination-info">
                          Page {historyPage} of {historyPages} &middot; {historyTotal} total
                        </span>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => setHistoryPage(p => Math.min(historyPages, p + 1))}
                          disabled={historyPage >= historyPages}
                        >
                          Next
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Unassigned Employees Tab */}
            {activeTab === 'unassigned' && (
              <div className="tab-content">
                <div className="search-toolbar">
                  <div className="search-input-wrapper">
                    <Icons.Search className="search-icon" />
                    <input
                      type="text"
                      placeholder="Search employees..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && fetchData()}
                    />
                  </div>
                  <Button variant="secondary" onClick={() => fetchData()}>
                    Search
                  </Button>
                </div>

                {unassignedEmployees.length === 0 ? (
                  <div className="empty-state">
                    <Icons.Users />
                    <p>All employees have project assignments</p>
                  </div>
                ) : (
                  <div className="employees-grid">
                    {unassignedEmployees.map(employee => (
                      <div key={employee.id} className="employee-card">
                        <div className="employee-info">
                          <span className="employee-name">{employee.name}</span>
                          <span className="employee-email">{employee.email}</span>
                          <span className="employee-code">{employee.employee_code}</span>
                        </div>
                        <div className="employee-meta">
                          {employee.department && (
                            <span className="department">{employee.department}</span>
                          )}
                          {employee.job_position && (
                            <span className="position">{employee.job_position}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </Card>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="modal-overlay" onClick={() => setShowRejectModal(false)}>
          <div className="modal-content modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Reject Request</h2>
              <button className="modal-close" onClick={() => setShowRejectModal(false)}>
                <Icons.X />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Reason (optional)</label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Enter rejection reason..."
                  rows={3}
                />
              </div>
            </div>
            <div className="modal-actions">
              <Button variant="secondary" onClick={() => setShowRejectModal(false)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={rejectingRequest?.type === 'project' ? handleRejectProjectRequest : handleRejectLateEntry}
              >
                Reject Request
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Allocations;
